<template>
  <Teleport to="#modal-portal-target">
    <Modal class="hotspot-product-modal" @close="activeHotspot = null">
      <div class="modal-image">
        <BaseImageExternal
          v-if="hotspot.fileUrl"
          :src="hotspot.fileUrl"
          class="hotspot-product-image"
        />
      </div>
      <template v-if="hasProductTag">
        <BaseText class="semi-bold product-name">{{
          hotspotProduct.productTag.name
        }}</BaseText>
        <BaseText>{{ hotspotProduct.productTag.description }}</BaseText>

        <BaseHr />

        <div class="product-discount">
          <div class="product-discount-price">
            <BaseText
              v-if="hotspotProduct.productTag.priceBeforeDiscount"
              class="dark-50 strikethrough"
            >
              {{
                currencyFormatter.format(
                  hotspotProduct.productTag.priceBeforeDiscount
                )
              }}
            </BaseText>
            <BaseText
              v-if="hotspotProduct.productTag.priceAfterDiscount"
              class="text-extra-large"
            >
              {{
                currencyFormatter.format(
                  hotspotProduct.productTag.priceAfterDiscount
                )
              }}
            </BaseText>
          </div>
          <BaseLabel v-if="hotspotProduct.productTag.discountPercent">{{
            $t("offers.discount-percent", [
              Math.trunc(hotspotProduct.productTag.discountPercent),
            ])
          }}</BaseLabel>
        </div>
      </template>
      <div v-if="hotspot.url" class="modal-buttons mt-1">
        <ShareButton
          v-if="hasProductTag"
          :item="{
            title: hotspotProduct.productTag.name,
            text: hotspotProduct.productTag.description,
            url: hotspot.shareUrl,
          }"
        />
        <a
          :href="hotspot.url"
          target="_blank"
          rel="noopener noreferrer"
          class="website-link"
          @click="createStatistic"
        >
          <BaseButton class="button-small website-button">
            {{ $t("hotspot.to-website") }}
          </BaseButton>
        </a>
      </div>
    </Modal>
  </Teleport>
</template>

<script setup lang="ts">
import { useRouteQuery } from "@vueuse/router";
import useTimer from "~/composables/useTimer";
import {
  type HotspotProductEntity,
  type HotspotLinkEntity,
  useCreateHotspotStatisticMutation,
  HotspotStatisticsType,
} from "~/graphql/generated";

const props = defineProps<{
  hotspot: HotspotProductEntity | HotspotLinkEntity;
}>();

const activeHotspot = useRouteQuery("offer");

const hasProductTag = computed(() =>
  Object.hasOwn(props.hotspot, "productTag")
);

const hotspotProduct = props.hotspot as unknown as HotspotProductEntity;

const { currencyFormatter } = useUtils();

// Hotspot statistics click mutation
const deviceId = useDeviceId();
const hotspotStatisticMutation = useCreateHotspotStatisticMutation();
const createStatistic = () => {
  hotspotStatisticMutation.executeMutation({
    createHotspotStatistic: {
      deviceId: deviceId.value,
      hotspotId: props.hotspot.id,
      statisticsType: HotspotStatisticsType.ProductLinkClick,
    },
  });
};

// Hotspot time visit mutation
const { startTimer, pauseTimer, resumeTimer, getTimeInSeconds } = useTimer();
startTimer();
const windowFocus = useWindowFocus();
watch(windowFocus, (isFocused) => {
  if (!isFocused) pauseTimer();
  if (isFocused) resumeTimer();
});
onUnmounted(() => {
  hotspotStatisticMutation.executeMutation({
    createHotspotStatistic: {
      deviceId: deviceId.value,
      hotspotId: props.hotspot.id,
      statisticsType: HotspotStatisticsType.ProductTimeSpent,
      secondsSpent: getTimeInSeconds(),
    },
  });
});

// Also (try to) send a statistics mutation event on the beforeunload (window close) event
onMounted(() => {
  useEventListener(document, "beforeunload", () => {
    hotspotStatisticMutation.executeMutation({
      createHotspotStatistic: {
        deviceId: deviceId.value,
        hotspotId: props.hotspot.id,
        statisticsType: HotspotStatisticsType.ProductTimeSpent,
        secondsSpent: getTimeInSeconds(),
      },
    });
  });
});
</script>

<style lang="scss" scoped>
.modal-image {
  max-height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.hotspot-product-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;
  margin: 0 auto;
}

.product-name {
  display: block;
}

.product-discount {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.product-discount-price {
  display: flex;
  flex-direction: column;
}

.modal-buttons {
  display: flex;
  gap: 1rem;
}

.website-link {
  flex: 1;
}
.website-button {
  width: 100%;
}
</style>
